import { config } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';

/**
 * MSW 초기화 결과 타입
 */
export interface MswInitResult {
  /** 초기화 성공 여부 */
  success: boolean;
  /** 실패 시 에러 객체 */
  error?: Error;
}

/**
 * MSW (Mock Service Worker) 초기화
 *
 * 다음 조건에서 MSW를 활성화합니다:
 * - 개발 환경 (import.meta.env.DEV)
 * - VITE_ENABLE_MSW=true 환경 변수 설정 시 (프로덕션에서도 Mock 사용)
 *
 * @benefits
 * - 관심사 분리: AppProvider에서 MSW 로직 분리
 * - 테스트 용이성: 독립적인 MSW 초기화 테스트 가능
 * - 재사용성: Storybook 등 다른 진입점에서도 사용 가능
 * - 유연성: 프로덕션에서도 Mock 데이터로 UI 테스트 가능
 *
 * @example
 * ```tsx
 * const { success, error } = await initMsw();
 * if (!success) {
 *   console.warn('MSW failed:', error);
 * }
 * ```
 */
export async function initMsw(): Promise<MswInitResult> {
  // MSW 비활성화 조건: 개발 모드가 아니고 VITE_ENABLE_MSW도 false
  if (!config.enableMsw) {
    return { success: true };
  }

  try {
    const { worker } = await import('@/test/mocks/browser');

    await worker.start({
      onUnhandledRequest: 'bypass', // 프로덕션에서는 모킹되지 않은 요청을 실제 서버로 전달
      quiet: config.isProd, // 프로덕션에서는 MSW 로그 비활성화
    });

    // 등록된 핸들러 수 로깅 (디버깅용, 개발 환경에서만)
    if (config.isDev) {
      const { allHandlers } = await import('@/test/mocks/handlers');
      console.log('[MSW] 등록된 핸들러 수:', allHandlers.length);
      console.log('[MSW] 핸들러 목록:');
      allHandlers.forEach((h, i) => {
        const method = h.info?.method || 'GET';
        const path = h.info?.path || 'unknown';
        console.log(`  [${i}] ${method} ${path}`);
      });
    }

    logger.info('MSW mocking enabled', {
      action: 'msw_init',
      metadata: { environment: config.isProd ? 'production' : 'development' },
    });
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    logger.error('MSW failed to start', error, {
      action: 'msw_init',
      metadata: { errorMessage },
    });

    // 테스트 환경에서는 MSW 실패가 치명적
    if (import.meta.env.MODE === 'test') {
      throw new Error(`MSW initialization failed in test environment: ${errorMessage}`);
    }

    return {
      success: false,
      error: error instanceof Error ? error : new Error(errorMessage),
    };
  }
}
